// src/components/CommentTree.tsx
import React from 'react';
import { RedditComment, RedditMoreComments } from '../types/reddit';
import Comment from './Comment';

interface CommentTreeProps {
    comments: (RedditComment | RedditMoreComments)[];
    postAuthor: string;
    onLoadMore: (moreData: RedditMoreComments['data']) => void;
    loadingMoreId: string | null;
    indentSize?: number; // Make indentSize optional at top level
}

const CommentTree: React.FC<CommentTreeProps> = ({
    comments,
    postAuthor,
    onLoadMore,
    loadingMoreId,
    // Increased default indent size significantly for more air between line and text
    indentSize = 20 // Changed from 16
}) => {
    if (!comments || comments.length === 0) return null;

    return (
        // Use space-y for vertical spacing between direct children (comments/more buttons)
        // --- CHANGE: Increased vertical spacing between comments ---
        <div className="space-y-8 comment-tree-level"> {/* Increased from space-y-6 or similar */}
            {comments.map((item) => {
                if (item.kind === 't1') {
                    return (
                        <Comment
                            key={item.data.id}
                            comment={item}
                            postAuthor={postAuthor}
                            onLoadMore={onLoadMore} // Pass down for potential nested 'more'
                            loadingMoreId={loadingMoreId}
                            indentSize={indentSize} // Pass the calculated size
                        />
                    );
                } else if (item.kind === 'more') {
                    const isLoadingThis = loadingMoreId === item.data.id;
                    // Calculate indent for the 'Load More' button itself
                    const itemPaddingLeft = `${item.data.depth * indentSize}px`;

                    return (
                        <div key={item.data.id} className="relative load-more-container" style={{ paddingLeft: itemPaddingLeft }}>
                             {/* Vertical Threading Line Stub for 'more' button */}
                             {item.data.depth > 0 && (
                                 <div
                                     className="absolute top-0 bottom-0 w-4" // Area for the line
                                     // Center line based on new indentSize
                                     style={{ left: `${(item.data.depth - 1) * indentSize + (indentSize / 2 - 1)}px` }}
                                     aria-hidden="true"
                                 >
                                    {/* Short vertical stub */}
                                    <span className="absolute left-[0px] top-2 h-4 w-px bg-gray-200 dark:bg-gray-700"></span>
                                 </div>
                             )}
                             {/* Load More Button - Simplified Style */}
                            <button
                                onClick={() => onLoadMore(item.data)}
                                disabled={isLoadingThis}
                                // --- CHANGE: Simplified button style ---
                                className={`text-xs font-medium inline-flex items-center h-5 ${ // Added fixed height h-5 to prevent jump
                                    isLoadingThis
                                    ? 'text-gray-500 dark:text-gray-400 cursor-wait'
                                    : 'text-indigo-600 dark:text-indigo-400 hover:underline focus:underline focus:ring-1 focus:ring-indigo-300 dark:focus:ring-indigo-600 rounded px-0.5' // Added rounded and slight padding on focus/hover
                                }`}
                            >
                                {isLoadingThis ? (
                                    <>
                                        <svg className="animate-spin -ml-0.5 mr-1.5 h-3.5 w-3.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                                        <span>Loading...</span>
                                    </>
                                ) : (
                                    // Display count if > 0, otherwise generic message
                                    <span>
                                        {item.data.count > 0 ? `Load ${item.data.count} more repl${item.data.count === 1 ? 'y' : 'ies'}` : 'Continue thread'}
                                    </span>
                                )}
                            </button>
                        </div>
                    );
                }
                return null; // Should not happen with valid data
            })}
        </div>
    );
};

export default CommentTree;